import { useState, useRef, useCallback, useEffect } from 'react';
import Recorder from 'recorder-core';
import { speech2Recognition } from '../../utils';

export const useAudioRecording = ({
    onRecordingStart,
    onRecognitionStart,
    onResult,
    onThen = () => {},
    onCatch = () => {},
    onVolumeChange,
    onError
} = {}) => {
    const [isRecording, setIsRecording] = useState(false);
    const [canRecord, setCanRecord] = useState(false);
    const [powerLevel, setPowerLevel] = useState(0);
    
    const recorderRef = useRef(null);
    const isRecordingRef = useRef(false);
    const _lan = useRef(null)
    const _during = useRef(null)
    const recordingStartTimeRef = useRef(null)
    const maxFileSizeRef = useRef(5 * 1024 * 1024) // 默认5MB限制
    // 同步状态到 ref
    useEffect(() => {
        isRecordingRef.current = isRecording;
    }, [isRecording]);

    // 截断文件大小的辅助函数
    const truncateFile = useCallback((file, maxSize) => {
        if (file.size <= maxSize) {
            return file;
        }
        
        console.log(`文件大小 ${(file.size / 1024 / 1024).toFixed(2)}MB 超过限制 ${(maxSize / 1024 / 1024).toFixed(2)}MB，开始截断`);
        
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const arrayBuffer = e.target.result;
                const truncatedBuffer = arrayBuffer.slice(0, maxSize);
                
                // 创建新的截断文件
                const truncatedFile = new File([truncatedBuffer], file.name, {
                    type: file.type,
                    lastModified: file.lastModified
                });
                
                console.log(`文件截断完成: ${(truncatedFile.size / 1024 / 1024).toFixed(2)}MB`);
                resolve(truncatedFile);
            };
            reader.readAsArrayBuffer(file);
        });
    }, []);

    // 检查麦克风权限
    const checkMicrophonePermission = useCallback(async () => {
        try {
            // 首先检查浏览器是否支持getUserMedia
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                throw new Error('NOT_SUPPORTED');
            }

            // 先尝试获取麦克风权限，这是最准确的检测方式
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach(track => track.stop());
            
            // 如果成功获取到流，再检查设备列表
            const devices = await navigator.mediaDevices.enumerateDevices();
            console.log(devices, 'devices')
            const audioDevices = devices.filter(device => device.kind === 'audioinput');
            
            if (audioDevices.length === 0) {
                throw new Error('NO_MICROPHONE');
            }

            setCanRecord(true);
            return { success: true };
        } catch (error) {
            console.error('麦克风权限检查失败:', error);
            setCanRecord(false);
            
            // 根据不同的错误类型返回相应的提示语
            let errorMessage = '';
            let errorDescription = '';
            
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                errorMessage = '麦克风访问权限被拒绝';
                errorDescription = '请手动点击浏览器左上角的网站信息，允许网页使用麦克风';
            } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                errorMessage = '未找到麦克风';
                errorDescription = '建议重新拔插外接麦克风，确保接口连接正常';
            } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
                errorMessage = '麦克风被其他应用程序占用';
                errorDescription = '请先关闭其他正在使用麦克风的网页或程序';
            } else if (error.name === 'NotSupportedError' || error.message === 'NOT_SUPPORTED') {
                errorMessage = '浏览器不支持麦克风功能';
                errorDescription = '请使用支持麦克风的现代浏览器';
            } else if (error.message === 'NO_MICROPHONE') {
                errorMessage = '未找到麦克风';
                errorDescription = '建议重新拔插外接麦克风，确保接口连接正常';
            } else if (error.name === 'AbortError' || error.name === 'SecurityError') {
                errorMessage = '麦克风无法启动';
                errorDescription = '建议重新启动计算机';
            } else {
                errorMessage = '麦克风无法启动';
                errorDescription = '建议重新启动计算机';
            }
            
            return { 
                success: false, 
                errorMessage, 
                errorDescription 
            };
        }
    }, []);
    
    // 开始录音
    const startRecording = useCallback(() => {
        if (isRecordingRef.current) {
            console.log('录音已在进行中');
            return;
        }
        
        try {
            const recorder = Recorder({
                type: 'wav',
                sampleRate: 16000,
                bitRate: 16,
                onProcess: (buffers, powerLevel, bufferDuration, bufferSampleRate) => {
                    // 实时更新音量
                    const adjustedLevel = Math.max(0, Math.min(100, powerLevel * 1.5));
                    setPowerLevel(adjustedLevel);
                    onVolumeChange?.(adjustedLevel);
                }
            });
            
            recorder.open(() => {
                recorder.start();
                setIsRecording(true);
                isRecordingRef.current = true;
                console.log('fuzhi', recorder)
                recorderRef.current = recorder;
                recordingStartTimeRef.current = performance.now(); // 记录录音开始时间
                onRecordingStart?.();
                console.log('录音开始');
            }, (msg) => {
                console.error('录音启动失败:', msg);
                onError?.(new Error(msg));
            });
            
        } catch (error) {
            console.error('录音初始化失败:', error);
            onError?.(error);
        }
    }, [onRecordingStart, onVolumeChange, onError]);
    
    // 处理录音完成后的语音识别
    const updateAudio = useCallback(async (latestAudioFile) => {
        if (latestAudioFile) {
            try {
                // 检查并截断文件大小
                const processedFile = await truncateFile(latestAudioFile, maxFileSizeRef.current);
                
                // 构建参数 - 使用 FormData 发送文件
                const formData = new FormData();
                formData.append('platform', 'lab');
                formData.append('recognize_type', 1);
                formData.append('lan', _lan.current);
                formData.append('during', _during.current);
                formData.append('extension_id', 'cxspeech2Text');
                formData.append('file', processedFile);
                console.log('录音完成，开始语音识别:', formData);
                
                // 触发识别开始回调
                onRecognitionStart?.();
                
                // 调用语音识别接口
                const res = await speech2Recognition(formData);
                onThen(res);
            } catch (error) {
                onCatch(error);
            }
        } else {
            onCatch?.();
        }
    }, [onThen, onCatch, onRecognitionStart, truncateFile]);

    // 停止录音
    const stopRecording = useCallback(() => {
        if (!isRecordingRef.current || !recorderRef.current) {
            console.log('当前未在录音');
            return;
        }
        recorderRef.current.stop((blob, duration) => {
            setIsRecording(false);
            setPowerLevel(0);
            isRecordingRef.current = false;
            recorderRef.current = null;
            recordingStartTimeRef.current = null; // 清理时间戳
            
            // 将 blob 转换为 File 对象
            const audioFile = new File([blob], `recording_${Date.now()}.wav`, {
                type: 'audio/wav',
                lastModified: Date.now()
            });
            // 上传给后台
            updateAudio(audioFile)
            onResult?.(audioFile, duration);
            console.log('录音停止');
        });
    }, [onResult, updateAudio]);

    // 自动停止录音（带定时器）
    const startCompleteRecognition = useCallback(({during = 1, lan = ''}) => {
        startRecording();
        _during.current = during
        _lan.current = lan
        
        // 使用高精度时间检查录音时长
        const checkRecordingDuration = () => {
            console.log(isRecordingRef.current , recorderRef.current , recordingStartTimeRef.current, '!!??')
            if (isRecordingRef.current && recorderRef.current && recordingStartTimeRef.current) {
                const currentTime = performance.now();
                const elapsedTime = (currentTime - recordingStartTimeRef.current) / 1000; // 转换为秒
                
                console.log(`录音已进行: ${elapsedTime.toFixed(2)}秒, 目标时长: ${during}秒`);
                 if (during && !Number.isNaN(Number(during)) && Number(during) === 60) {
                    during = 59
                }
                if (elapsedTime >= during) {
                    console.log('录音时长达到目标，停止录音');
                    stopRecording();
                } else {
                    // 继续检查，使用较短的间隔以获得更精确的控制
                    setTimeout(checkRecordingDuration, 100); // 每50ms检查一次
                }
            } else if (isRecordingRef.current && recorderRef.current) {
                console.log('1')
                // 录音已启动但时间戳还未设置，继续等待
                setTimeout(checkRecordingDuration, 100);
                // if (performance.now() - recordingStartTimeRef.current < 5000) {
                // } 
            } else {
                console.log('2');
                // 录音还未启动，继续等待
                setTimeout(checkRecordingDuration, 100);
                // if (performance.now() - recordingStartTimeRef.current < 5000) {
                // }
            }
        };
        
        checkRecordingDuration();
    }, [startRecording, stopRecording]);
    
    // 清理资源
    useEffect(() => {
        return () => {
            if (recorderRef.current) {
                recorderRef.current.close();
            }
        };
    }, []);
    
    // 设置最大文件大小限制
    const setMaxFileSize = useCallback((maxSizeInMB) => {
        maxFileSizeRef.current = maxSizeInMB * 1024 * 1024;
        console.log(`设置最大文件大小限制为: ${maxSizeInMB}MB`);
    }, []);

    return {
        isRecording,
        canRecord,
        powerLevel,
        checkMicrophonePermission,
        startRecording,
        stopRecording,
        startCompleteRecognition,
        setMaxFileSize
    };
};
